import { MIN_PLAYERS, MAX_PLAYERS, ROLES, ROLE_TIPS } from '@betrayal/shared';
import { useStore } from '../store.js';
import { getTheme, THEMES } from '../themes.js';

const KOFI_URL = 'https://ko-fi.com/betrayalgame';

export function WaitingRoom() {
  const { lobby, you, send, bridge } = useStore();
  if (!lobby) return null;
  const me = lobby.members.find((m) => m.id === you);
  const isHost = me?.isHost ?? false;
  const canStart = lobby.members.filter((m) => m.connected).length >= MIN_PLAYERS;
  const theme = getTheme(lobby.config.themeId);

  return (
    <div className="waiting">
      <header className="brand">
        <h1 className="logo">The Court of Shadows</h1>
        <p className="muted tagline">Bluff. Accuse. Survive.</p>
      </header>

      <div className="panel">
        <h2>
          At the table ({lobby.members.length}/{MAX_PLAYERS})
        </h2>
        <ul className="member-list">
          {lobby.members.map((m) => (
            <li key={m.id} className={m.connected ? '' : 'disconnected'}>
              {m.avatarUrl ? <img src={m.avatarUrl} alt="" /> : <span className="avatar-fallback">🎭</span>}
              <span>{m.name}</span>
              {m.isHost && <span className="tag">host</span>}
              {m.id === you && <span className="tag you">you</span>}
            </li>
          ))}
        </ul>
      </div>

      <div className="panel">
        <h2>Table rules</h2>
        <label className="row">
          <span>Setting</span>
          <select
            disabled={!isHost}
            value={lobby.config.themeId}
            onChange={(e) => send({ t: 'SET_CONFIG', themeId: e.target.value })}
          >
            {THEMES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label className="row">
          <span>Turn timer</span>
          <select
            disabled={!isHost}
            value={lobby.config.turnTimer}
            onChange={(e) => send({ t: 'SET_CONFIG', turnTimer: Number(e.target.value) })}
          >
            <option value={0}>off</option>
            <option value={30}>30s</option>
            <option value={60}>60s</option>
            <option value={120}>2min</option>
          </select>
        </label>
        <label className="row">
          <span>Reaction timer</span>
          <select
            disabled={!isHost}
            value={lobby.config.reactionTimer}
            onChange={(e) => send({ t: 'SET_CONFIG', reactionTimer: Number(e.target.value) })}
          >
            <option value={0}>off</option>
            <option value={10}>10s</option>
            <option value={15}>15s</option>
            <option value={30}>30s</option>
          </select>
        </label>
        {!isHost && <p className="muted small">Only the host can change the table rules.</p>}
      </div>

      <div className="panel setting-panel">
        <h2>{theme.name}</h2>
        {theme.intro && <p className="intro">{theme.intro}</p>}
      </div>

      <div className="panel">
        <h2>The cast — who's who</h2>
        <ul className="cast">
          {ROLES.map((r) => {
            const role = theme.roles[r];
            return (
              <li key={r}>
                <span className="cast-glyph" style={{ borderColor: role.color }}>
                  {role.glyph}
                </span>
                <span className="cast-body">
                  <span className="cast-name">{role.name}</span>
                  <span className="cast-tip">{ROLE_TIPS[r]}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <details className="panel how-to">
        <summary>How to play</summary>
        <div className="how-to-body">
          <p>
            Everyone holds two hidden character cards. Lose both and you're out — last player
            standing wins.
          </p>
          <p>
            On your turn you take one action. Some just take coins; others claim a character's
            power. <strong>You can claim any character, whether you hold it or not</strong> —
            that's the bluff.
          </p>
          <p>
            When someone claims a power, anyone may <strong>challenge</strong> it. If the
            claimant was lying, they lose a card — if they were telling the truth, the
            challenger does. Some actions can also be <strong>blocked</strong> by claiming the
            right character (which can itself be challenged).
          </p>
          <p>
            Reach 7 coins to force an opponent to lose a card; at 10 coins you must. Hover any
            card or action in-game for a reminder of what it does.
          </p>
        </div>
      </details>

      {isHost ? (
        <button className="primary big" disabled={!canStart} onClick={() => send({ t: 'START_GAME' })}>
          {canStart ? 'Start the game' : `Waiting for players (${MIN_PLAYERS}+ needed)`}
        </button>
      ) : (
        <p className="muted">Waiting for the host to start…</p>
      )}

      <footer className="footer">
        <a
          href={KOFI_URL}
          onClick={(e) => {
            e.preventDefault();
            bridge?.openExternal(KOFI_URL);
          }}
        >
          ☕ Enjoying it? Buy us AI tokens
        </a>
      </footer>
    </div>
  );
}
